import { useCallback, useEffect, useState } from 'react'
import type { Api } from '../api'
import { ApiError, type ApiFailure } from '../api/types'
import type { DevFlag, DevFlagKey } from '../api/types'
import { Card, EmptyState, Loading, Notice, inputClass } from '../components/ui'
import { FailureState } from '../components/FailureState'
import { InfoStrip, Panel, StateDot } from './accessWords'

/**
 * Developer mode — the switches.
 *
 * WHAT IT IS FOR. Whether the platform's figures are correct in BUSINESS terms
 * is not a question the people who built it can answer. This turns on a panel
 * that explains every documented figure on a report — what it means, which
 * source column it comes from, the maths, and which procedure produced the
 * number on screen — so somebody who understands the business and not this
 * codebase can check it.
 *
 * PER PERSON. Each System Admin's switches are their own: turning them on or off
 * changes nothing on anybody else's screen. Every change is still recorded in
 * aud.Security_Audit_MBI with the actor and the time, and with a note when one is
 * given. The note is optional on purpose: a mandatory justification for an act
 * that affects nobody else is ceremony, and ceremony is what teaches people to
 * type "asdf" into a required field.
 *
 * Laid out again 27 Oct 2026 with the other Access pages: the master switch as
 * the page's one big answer, then one card per section of the panel, each saying
 * what it adds and whether it will show. A section saved as on while the master is
 * off says it is waiting, rather than looking on in grey.
 */
export function DeveloperModeTab({ api }: { api: Api }) {
  const [flags, setFlags] = useState<DevFlag[] | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState<DevFlagKey | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  // WHY the read failed, kept rather than thrown away. It used to be swallowed
  // into an empty list and every failure shown as one guess, "needs 028
  // deployed" -- so a 500 from a missing EXECUTE grant (028 in place, 002 not
  // re-run after it) sent the admin to redeploy the one thing already done, with
  // no reference to find the logged error by.
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    api
      .devFlags()
      .then((f) => {
        if (!cancelled) {
          setFailure(null)
          setFlags(f)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'The switches could not be read.' })
          // Still set, only so the Loading branch ends; the failure above is
          // what gets rendered, not this empty list.
          setFlags([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [api])

  useEffect(load, [load])

  const at = (key: DevFlagKey) => flags?.find((f) => f.key === key)
  const master = at('dev_mode')
  const on = Boolean(master?.enabled)

  const flip = async (key: DevFlagKey, enabled: boolean) => {
    setError(null)
    setDone(null)
    setBusy(key)
    try {
      await api.setDevFlag({ key, enabled, reason: reason.trim() })
      setDone(`${LABELS[key].title} is now ${enabled ? 'on' : 'off'}.`)
      setReason('')
      load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The change was refused.')
    } finally {
      setBusy(null)
    }
  }

  if (flags === null) return <Loading label="Reading the switches…" />

  if (flags.length === 0) {
    // For an admin the procedure always returns four rows, so an empty list only
    // ever means the read failed. Show the API's own words and reference, and a
    // retry where one can help. FailureState has a case for every kind,
    // 'not-found' included, so the EmptyState below is only a floor for an
    // empty list that arrived with no failure at all.
    if (failure) {
      return (
        <FailureState
          failure={failure}
          onRetry={() => {
            setFlags(null)
            load()
          }}
        />
      )
    }
    return (
      <EmptyState
        title="Developer mode is unavailable"
        detail="The switches could not be read. This screen is System Admin only, and needs 028_measure_docs.sql deployed."
      />
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {(error || done) && (
        <div className="shrink-0 space-y-2">
          {error && <Notice tone="bad" onDismiss={() => setError(null)}>{error}</Notice>}
          {done && <Notice tone="good" onDismiss={() => setDone(null)}>{done}</Notice>}
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto bg-(--color-surface-2) px-5 py-4 scroll-slim">
          {/* THE ANSWER FIRST: is it on for me, and what does that mean on a report. */}
          <section
            className={`flex flex-col gap-4 rounded-md border px-4 py-4 @5xl:flex-row @5xl:items-center ${
              on ? 'border-(--color-good)/35 bg-(--color-good)/6' : 'border-(--color-line) bg-(--color-surface)'}`}
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <StateDot ok={on ? true : null} />
              <div className="flex min-w-0 flex-col gap-1">
                <h3 className="text-(length:--text-lg) font-semibold text-(--color-ink)">
                  Developer mode is {on ? 'on' : 'off'} for you
                </h3>
                <p className="text-(length:--text-small) leading-relaxed text-(--color-ink-2)">
                  {on
                    ? 'On every report, click a column header to open a panel explaining the figure: what it means, and the parts you have turned on below.'
                    : 'Turn it on to click a column header on any report and see what the figure means, where it comes from and how it is calculated. With it off, the panel never opens and the API answers as though the feature did not exist.'}
                </p>
                {master?.changedUtc && (
                  <p className="text-(length:--text-micro) text-(--color-ink-3)">You last changed this on {changedOn(master.changedUtc)}.</p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2 @5xl:w-[340px]">
              {/* OPTIONAL, and beside the switches rather than in a dialog after
                  one is clicked: recorded with whichever switch moves next. */}
              <label className="flex flex-col gap-1">
                <span className="eyebrow">Note for the record (optional)</span>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. checking the Sales margin figures"
                  className={inputClass}
                />
              </label>
              <BigSwitch
                on={on}
                busy={busy === 'dev_mode'}
                label="Developer mode"
                onFlip={(v) => void flip('dev_mode', v)}
              />
            </div>
          </section>

          <Panel title="What the panel shows" aside={<span className="text-(length:--text-small) text-(--color-ink-3)">One switch for each part</span>}>
            <div className="grid grid-cols-1 gap-2 @5xl:grid-cols-2 @6xl:grid-cols-4">
              {/* Always there once the master is on: not a switch of its own. */}
              <PartCard
                section="What it means"
                what="The figure in plain words, and the caveats that decide whether it means what it appears to."
                state={on ? 'shows' : 'off'}
              />
              {(['dev_show_source', 'dev_show_math', 'dev_show_query'] as const).map((k) => {
                const flag = at(k)
                const saved = Boolean(flag?.enabled)
                return (
                  <PartCard
                    key={k}
                    section={LABELS[k].section}
                    what={LABELS[k].what}
                    state={!saved ? 'off' : on ? 'shows' : 'waiting'}
                    changedUtc={flag?.changedUtc ?? null}
                    toggle={{
                      label: LABELS[k].title,
                      on: saved,
                      busy: busy === k,
                      // Disabled rather than hidden while the master is off, so the
                      // reader can see what turning it on would reveal first.
                      disabled: !on,
                      onFlip: (v) => void flip(k, v),
                    }}
                  />
                )
              })}
            </div>
          </Panel>
        </div>

        <InfoStrip>
          These switches are yours alone: another System Admin turning theirs on or off does nothing to your screen.
          The panel shows SQL as text; it never runs any. Every change is recorded with the time and your note.
        </InfoStrip>
      </Card>
    </div>
  )
}

const LABELS: Record<DevFlagKey, { title: string; section: string; what: string }> = {
  dev_mode: {
    title: 'Developer mode',
    section: 'What it means',
    what: 'The master switch. With it off, the panel never opens and the API answers as though the feature did not exist.',
  },
  dev_show_source: {
    title: 'Show data source',
    section: 'Where it comes from',
    what: 'Which view and which physical columns a figure is read from — the fact a reviewer can check against the ERP.',
  },
  dev_show_math: {
    title: 'Show math',
    section: 'How it is calculated',
    what: 'The calculation, written as a person would write it. CI re-checks it against the live procedure on every build, so it cannot silently go out of date.',
  },
  dev_show_query: {
    title: 'Show query',
    section: 'What this screen asked for',
    what: 'Which stored procedure produced the number, and what this screen asked it for. Text only — never a query plan, which would carry real customer names.',
  },
}

/* "You changed this", not a name. The switch is per person, so the only name it
   could ever carry is the reader's own — printing their own email back at them
   would imply somebody else might appear here. With the year: a switch moved last
   year must not read as this year's. */
function changedOn(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The master switch, as a wide control that says what pressing it does. */
function BigSwitch({ on, busy, label, onFlip }: { on: boolean; busy: boolean; label: string; onFlip: (on: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy}
      onClick={() => onFlip(!on)}
      className={`flex items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors disabled:cursor-wait ${
        on ? 'border-(--color-navy-700) bg-(--color-navy-50) hover:bg-(--color-navy-100)'
          : 'border-(--color-line-strong) bg-(--color-surface) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'}`}
    >
      <Knob on={on} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-(length:--text-small) font-semibold text-(--color-ink)">{busy ? 'Saving…' : on ? 'On' : 'Off'}</span>
        <span className="text-(length:--text-micro) text-(--color-ink-3)">{on ? 'Press to turn it off' : 'Press to turn it on'}</span>
      </span>
    </button>
  )
}

function Knob({ on, small = false }: { on: boolean; small?: boolean }) {
  return (
    <span aria-hidden className={`relative inline-flex shrink-0 items-center rounded-full transition-colors ${small ? 'h-5 w-9' : 'h-6 w-11'} ${
      on ? 'bg-(--color-navy-700)' : 'bg-(--color-ink-4)'}`}>
      <span className={`absolute rounded-full bg-white shadow transition-transform motion-reduce:transition-none ${small ? 'h-4 w-4' : 'h-5 w-5'} ${
        on ? (small ? 'translate-x-[18px]' : 'translate-x-[22px]') : 'translate-x-0.5'}`} />
    </span>
  )
}

/**
 * One part of the explain panel: its heading as the panel prints it, what it adds,
 * whether it will show, and its switch. "Waiting" is a part saved as on while the
 * master is off: it shows the moment Developer mode does.
 */
function PartCard({ section, what, state, changedUtc = null, toggle }: {
  section: string
  what: string
  state: 'shows' | 'waiting' | 'off'
  changedUtc?: string | null
  toggle?: { label: string; on: boolean; busy: boolean; disabled: boolean; onFlip: (on: boolean) => void }
}) {
  const chip = state === 'shows'
    ? <span className="rounded-full bg-(--color-good)/12 px-1.5 text-(length:--text-micro) font-semibold text-(--color-good)">Shows</span>
    : state === 'waiting'
      ? <span className="rounded-full bg-(--color-surface-3) px-1.5 text-(length:--text-micro) font-semibold text-(--color-ink-3)" title="Saved as on. It shows once Developer mode is on.">Waits for Developer mode</span>
      : <span className="rounded-full bg-(--color-surface-3) px-1.5 text-(length:--text-micro) font-semibold text-(--color-ink-4)">Hidden</span>
  return (
    <div className={`flex flex-col gap-2 rounded-md border px-3 py-3 ${state === 'shows' ? 'border-(--color-line-strong) bg-(--color-surface)' : 'border-(--color-line) bg-(--color-surface)'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-(--color-ink)">{section}</span>
        {chip}
      </div>
      <p className="flex-1 text-(length:--text-small) leading-snug text-(--color-ink-2)">{what}</p>
      {toggle ? (
        <button
          type="button"
          role="switch"
          aria-checked={toggle.on}
          aria-label={toggle.label}
          disabled={toggle.disabled || toggle.busy}
          onClick={() => toggle.onFlip(!toggle.on)}
          title={toggle.disabled ? 'Turn Developer mode on first.' : undefined}
          className="flex items-center gap-2.5 rounded-md border border-(--color-line) px-2.5 py-1.5 text-left transition-colors hover:border-(--color-navy-300) hover:bg-(--color-navy-50) disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:border-(--color-line) disabled:hover:bg-transparent"
        >
          <Knob on={toggle.on} small />
          <span className="flex min-w-0 flex-col">
            <span className="text-(length:--text-small) font-medium text-(--color-ink)">{toggle.busy ? 'Saving…' : toggle.label}</span>
            {changedUtc && <span className="text-(length:--text-micro) text-(--color-ink-3)">You last changed this on {changedOn(changedUtc)}</span>}
          </span>
        </button>
      ) : (
        <span className="flex items-center gap-2 rounded-md border border-dashed border-(--color-line) px-2.5 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
          Comes with Developer mode
        </span>
      )}
    </div>
  )
}
